import { FINANCE_ENTRY_TYPE_LABELS, type FinanceEntryTypeValue } from '@crm/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Label,
  Modal,
  Select,
  Spinner,
} from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Pencil, Plus, Tags, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ConfirmDialog, FormField, PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import {
  type FinanceCategory,
  useCreateFinanceCategory,
  useDeleteFinanceCategory,
  useFinanceCategories,
  useUpdateFinanceCategory,
} from '../api';

const FORM_ID = 'finance-category-form';

const categoryFormSchema = z.object({
  name: z.string().min(2, 'Informe o nome').max(60),
  type: z.enum(['PAYABLE', 'RECEIVABLE']),
  isActive: z.boolean(),
});

type CategoryFormValues = z.infer<typeof categoryFormSchema>;

const EMPTY: CategoryFormValues = { name: '', type: 'PAYABLE', isActive: true };

const TYPES: FinanceEntryTypeValue[] = ['PAYABLE', 'RECEIVABLE'];

/** Quantas categorias de cada lado entram na tela por vez. */
const PAGINA = 20;

export function FinanceCategoriesPage() {
  useDocumentTitle('Categorias financeiras');

  const [visiveis, setVisiveis] = useState<Partial<Record<FinanceEntryTypeValue, number>>>({});
  const canWrite = useCan('finance', 'WRITE');
  const categories = useFinanceCategories();
  const createCategory = useCreateFinanceCategory();
  const updateCategory = useUpdateFinanceCategory();
  const deleteCategory = useDeleteFinanceCategory();

  const [editing, setEditing] = useState<FinanceCategory | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<FinanceCategory | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: EMPTY,
  });

  const mutationError = createCategory.error ?? updateCategory.error ?? deleteCategory.error;

  // Cada abertura comeca do estado certo: a categoria em edicao ou em branco.
  useEffect(() => {
    if (!isFormOpen) return;

    reset(editing ? { name: editing.name, type: editing.type, isActive: editing.isActive } : EMPTY);
  }, [isFormOpen, editing, reset]);

  function openForm(category: FinanceCategory | null) {
    setEditing(category);
    setIsFormOpen(true);
  }

  function closeForm() {
    setIsFormOpen(false);
    setEditing(null);
  }

  function onSubmit(values: CategoryFormValues) {
    if (editing) {
      updateCategory.mutate({ id: editing.id, input: values }, { onSuccess: closeForm });
      return;
    }

    createCategory.mutate(values, { onSuccess: closeForm });
  }

  const all = categories.data?.data ?? [];
  const isSaving = createCategory.isPending || updateCategory.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Tags />}
        title="Categorias financeiras"
        description="Como as contas sao classificadas nos relatorios. A empresa ja nasce com as do dia a dia; edite e crie as suas."
        actions={
          canWrite ? (
            <Button onClick={() => openForm(null)}>
              <Plus className="h-4 w-4" />
              <span>
                Nova<span className="hidden sm:inline"> categoria</span>
              </span>
            </Button>
          ) : null
        }
      />

      {mutationError ? (
        <p className="text-sm text-destructive">{toErrorMessage(mutationError)}</p>
      ) : null}

      {categories.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="h-6 w-6 text-muted-foreground" />
        </div>
      ) : categories.isError ? (
        <p className="text-sm text-destructive">{toErrorMessage(categories.error)}</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {TYPES.map((type) => {
            const todas = all.filter((category) => category.type === type);
            // A lista vem inteira do servidor; a tela mostra um lote por vez
            // para nao virar uma coluna sem fim quando o plano de contas
            // crescer.
            const items = todas.slice(0, visiveis[type] ?? PAGINA);

            return (
              <Card key={type} variant="flush">
                <CardHeader>
                  <CardTitle className="text-base">{FINANCE_ENTRY_TYPE_LABELS[type]}</CardTitle>
                </CardHeader>
                <CardContent>
                  {items.length > 0 ? (
                    <ul className="divide-y divide-border">
                      {items.map((category) => (
                        <li
                          key={category.id}
                          className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
                        >
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <p className="font-medium">{category.name}</p>
                            <Badge variant="outline">
                              {category.entriesCount}{' '}
                              {category.entriesCount === 1 ? 'conta' : 'contas'}
                            </Badge>
                            {category.isActive ? null : (
                              <Badge variant="destructive">Inativa</Badge>
                            )}
                          </div>

                          {canWrite ? (
                            <div className="flex shrink-0 items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Editar"
                                onClick={() => openForm(category)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Excluir"
                                onClick={() => setPendingRemoval(category)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="py-4 text-center text-sm text-muted-foreground">
                      Nenhuma categoria deste tipo.
                    </p>
                  )}

                  {todas.length > items.length ? (
                    <div className="flex flex-col items-center gap-1 pt-3">
                      <Button
                        variant="ghost"
                        className="w-full text-primary sm:w-auto"
                        onClick={() =>
                          setVisiveis((atual) => ({
                            ...atual,
                            [type]: (atual[type] ?? PAGINA) + PAGINA,
                          }))
                        }
                      >
                        <Plus className="h-4 w-4" />
                        Mostrar mais
                      </Button>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={isFormOpen}
        onClose={closeForm}
        title={editing ? 'Editar categoria' : 'Nova categoria'}
        subtitle="A categoria fica de um lado so: despesa nao entra em conta a receber."
        size="small"
        actions={[
          { label: 'Cancelar', variant: 'ghost', onClick: closeForm },
          {
            label: editing ? 'Salvar' : 'Criar',
            type: 'submit',
            formId: FORM_ID,
            loading: isSaving,
          },
        ]}
      >
        <form id={FORM_ID} className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormField label="Nome" error={errors.name?.message} {...register('name')} />

          <div className="space-y-2">
            <Label htmlFor="type">Tipo</Label>
            <Select id="type" {...register('type')}>
              {TYPES.map((type) => (
                <option key={type} value={type}>
                  {FINANCE_ENTRY_TYPE_LABELS[type]}
                </option>
              ))}
            </Select>
          </div>

          <Checkbox label="Categoria ativa" {...register('isActive')} />
        </form>
      </Modal>

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Excluir categoria"
        message={`A categoria ${pendingRemoval?.name ?? ''} sai do financeiro. Com contas vinculadas, prefira desativar.`}
        loading={deleteCategory.isPending}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) deleteCategory.mutate(pendingRemoval.id);
          setPendingRemoval(null);
        }}
      />
    </div>
  );
}
